<template>
  <div class="appointment__calendar">
    <!-- resumo do agendamento: data/hora + urgência ao lado do preço -->
    <div v-if="hasDate" class="appointment__calendar-summary mb-3">
      <p class="subtitle-2 font-weight-bold mb-2">
        {{ $t('appointment.formCreate.summary.title') }}
      </p>

      <div class="d-flex align-start justify-space-between">
        <div>
          <p class="caption font-weight-bold mb-0">
            {{ $t('appointment.formCreate.summary.dateTime') }}
          </p>
          <p class="body-2 mb-0">
            {{ dateTimeLabel }}
          </p>
          <div v-if="isUrgent" class="d-flex align-center mt-1">
            <v-chip
              class="pulse-effect mr-2"
              x-small
              color="error"
            >
              {{ $t('appointment.formCreate.summary.urgent') }}
            </v-chip>
            <InformationTooltip
              :message="$t('appointment.formCreate.summary.urgencyDescription')"
              small
            />
          </div>
        </div>

        <v-card
          v-if="shouldShowTotalPrice"
          class="appointment__calendar-price py-2 px-4 ml-3 green lighten-4 green--text flex-shrink-0"
          elevation="0"
        >
          <p class="mb-0 caption font-weight-bold">
            {{ $t('appointment.formCreate.titles.totalAmount') }}
          </p>
          <p class="mb-0 subtitle-1 font-weight-bold">
            {{ formatMoney(appointmentAmount) }}
          </p>
        </v-card>
      </div>

      <v-divider class="mt-3" />
    </div>

    <!-- título -->
    <p class="appointment__calendar-title subtitle-2 font-weight-bold mb-2">
      {{ $t('appointment.formCreate.calendar.title') }}
    </p>

    <!-- filtros: imóvel, trabalhador e modo de visualização (1 ou 3 dias) -->
    <AppointmentCalendarFilters
      class="mb-3"
      :days-in-window.sync="daysInWindow"
      :host-ids.sync="hostIds"
      :realty-ids.sync="realtyIds"
    />

    <!-- header: navegação -->
    <div class="appointment__calendar-header d-flex align-center justify-space-between mb-2">
      <v-btn
        icon
        small
        :disabled="isAtMinimum"
        :aria-label="$t('appointment.formCreate.calendar.prev')"
        @click="prev"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <span class="caption font-weight-bold text-center">
        {{ rangeLabel }}
      </span>

      <v-btn
        icon
        small
        :aria-label="$t('appointment.formCreate.calendar.next')"
        @click="next"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <v-progress-linear
      v-if="isCalendarLoading"
      height="2"
      class="mb-1"
      indeterminate
    />

    <!-- calendário 3 dias -->
    <v-calendar
      ref="calendar"
      class="appointment__calendar-body"
      type="custom-daily"
      :start="startDate"
      :end="endDate"
      :events="events"
      :event-color="getEventColor"
      :event-ripple="false"
      :first-interval="firstInterval"
      :interval-count="intervalCount"
      :interval-height="56"
      interval-width="44"
      locale="pt-br"
    >
      <template v-slot:event="{ event }">
        <v-tooltip
          left
          color="grey darken-3"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              class="appointment__calendar-event pa-1"
              :class="{ 'appointment__calendar-event--assumed': event.endAssumed }"
              v-on="on"
            >
              <div class="d-flex align-start justify-space-between">
                <strong
                  v-if="event.neighborhood"
                  class="appointment__calendar-event-neighborhood"
                >
                  {{ event.neighborhood }}
                </strong>
                <v-icon
                  v-if="event.endAssumed"
                  x-small
                  color="grey darken-2"
                >
                  mdi-clock-alert-outline
                </v-icon>
              </div>
              <div
                v-if="event.hostName"
                class="appointment__calendar-event-line"
              >
                {{ event.hostName }}
              </div>
              <div
                v-for="(address, index) in event.addresses"
                :key="index"
                class="appointment__calendar-event-address"
              >
                {{ address }}
              </div>
            </div>
          </template>

          <!-- tooltip: detalhe completo (mitiga aperto do bloco) -->
          <div class="appointment__calendar-tooltip">
            <p class="mb-1 font-weight-bold">
              {{ event.code }}
            </p>
            <p class="mb-1">
              {{ $t(`status.${event.status}`) }}
            </p>
            <p class="mb-1">
              {{ event.timeLabel }}
            </p>
            <p
              v-if="event.hostName"
              class="mb-1"
            >
              {{ event.hostName }}
            </p>
            <p
              v-for="(address, index) in event.addresses"
              :key="index"
              class="mb-0"
            >
              {{ address }}
            </p>

            <!-- seta apontando para o card (balão de fala) -->
            <span class="appointment__calendar-tooltip-arrow" />
          </div>
        </v-tooltip>
      </template>
    </v-calendar>
  </div>
</template>

<script>
  import moment from 'moment'
  import { mapGetters, mapActions } from 'vuex'
  import { handleColors } from '@/util/colors'
  import { extractNeighborhood } from '@/util/address'
  import { catalogDate, catalogMonetary } from '@/util/catalog'
  import InformationTooltip from '../app/InformationTooltip.vue'
  import AppointmentCalendarFilters from './AppointmentCalendarFilters.vue'

  const DEFAULT_SLOT_MINUTES = 60

  export default {
    name: 'AppointmentCalendarPanel',

    components: {
      InformationTooltip,
      AppointmentCalendarFilters,
    },

    data () {
      return {
        // início da janela (00:00). Default = hoje.
        focusStart: moment().startOf('day'),
        // tamanho da janela: 1 ou 3 dias
        daysInWindow: 3,
        // filtros
        hostIds: [],
        realtyIds: [],
        formatDate: catalogDate,
        formatMoney: catalogMonetary,
      }
    },

    computed: {
      ...mapGetters([
        'calendarAppointments',
        'isCalendarLoading',
        'appointmentFields',
        'appointmentAmount',
        'currentServiceContract',
        'user',
        'currentRealEstate',
      ]),

      // --- resumo do agendamento (data/hora + urgência + preço) ---
      hasDate () {
        return !!this.appointmentFields?.date && this.appointmentFields.date.length >= 11
      },

      dateTimeLabel () {
        const date = this.formatDate(this.appointmentFields.date)
        return this.endTimeSummary ? `${date} ~ ${this.endTimeSummary}` : date
      },

      endTimeSummary () {
        const { date, slot_size } = this.appointmentFields
        if (!date || !slot_size) return null
        return moment(date).add(slot_size, 'minutes').format('HH:mm')
      },

      isUrgent () {
        const realEstate = this.user?.real_estate
        const isCustomerWithoutSubscription = [
          'SINGLE_PURCHASE',
          'PREPAID',
          'PREPAID_SAAS',
        ].includes(realEstate?.subscription_type)

        let hoursMargin = isCustomerWithoutSubscription ? 24 : 16

        const { urgency_tax, urgency_hours_limit } =
          this.currentServiceContract || {}

        if (urgency_tax > 0 && urgency_hours_limit > 0) {
          hoursMargin = urgency_hours_limit
        }

        const dateWithMargin = new Date()
        dateWithMargin.setHours(dateWithMargin.getHours() + hoursMargin)

        return new Date(this.appointmentFields.date) < dateWithMargin
      },

      isInternalWorker () {
        return this.appointmentFields.worker_type === 'INTERNAL'
      },

      shouldShowTotalPrice () {
        return (
          this.appointmentAmount &&
          !this.currentRealEstate?.use_custom_worker_type &&
          !this.isInternalWorker
        )
      },

      startDate () {
        return this.focusStart.format('YYYY-MM-DD')
      },

      endDate () {
        return this.focusStart
          .clone()
          .add(this.daysInWindow - 1, 'days')
          .format('YYYY-MM-DD')
      },

      // não deixa navegar para antes do dia atual
      isAtMinimum () {
        return this.focusStart.isSameOrBefore(moment().startOf('day'), 'day')
      },

      rangeLabel () {
        const start = this.focusStart.clone()
        const end = this.focusStart.clone().add(this.daysInWindow - 1, 'days')
        return start.isSame(end, 'day')
          ? start.format('DD/MM')
          : `${start.format('DD/MM')} - ${end.format('DD/MM')}`
      },

      events () {
        return (this.calendarAppointments || []).map((appointment) => {
          const start = moment(appointment.date)
          const hasSlot = !!appointment.slot_size
          const end = start
            .clone()
            .add(hasSlot ? appointment.slot_size : DEFAULT_SLOT_MINUTES, 'minutes')

          const addresses = (appointment.realties || [])
            .map((realty) => realty.address)
            .filter(Boolean)

          // data + hora; hora final só quando há slot_size
          const timeLabel = hasSlot
            ? `${start.format('DD/MM/YYYY HH:mm')} ~ ${end.format('HH:mm')}`
            : start.format('DD/MM/YYYY HH:mm')

          return {
            // usado pelo v-calendar para posicionar
            start: start.format('YYYY-MM-DD HH:mm'),
            end: end.format('YYYY-MM-DD HH:mm'),
            timed: true,
            // dados exibidos no slot custom
            code: appointment.code,
            status: appointment.status,
            hostName: appointment.host?.name || '',
            addresses,
            neighborhood: extractNeighborhood(addresses[0]),
            endAssumed: !hasSlot,
            timeLabel,
            color: handleColors(appointment.status),
          }
        })
      },

      // janela de horas mostrada, calculada a partir dos eventos (fallback 8h-18h)
      intervalBounds () {
        const hours = this.events.reduce(
          (acc, event) => {
            const startHour = moment(event.start, 'YYYY-MM-DD HH:mm').hour()
            const endHour = moment(event.end, 'YYYY-MM-DD HH:mm').hour()
            return {
              min: Math.min(acc.min, startHour),
              max: Math.max(acc.max, endHour + 1),
            }
          },
          { min: 8, max: 18 },
        )

        const first = Math.max(0, hours.min - 1)
        const count = Math.min(24 - first, Math.max(1, hours.max - first))
        return { first, count }
      },

      firstInterval () {
        return this.intervalBounds.first
      },

      intervalCount () {
        return this.intervalBounds.count
      },
    },

    watch: {
      startDate: {
        immediate: true,
        handler () {
          this.fetch()
        },
      },

      // mudar o tamanho da janela altera o range buscado
      daysInWindow () {
        this.fetch()
      },

      // filtros re-disparam a busca
      hostIds () {
        this.fetch()
      },

      realtyIds () {
        this.fetch()
      },
    },

    methods: {
      ...mapActions(['fetchCalendarAppointments']),

      fetch () {
        const startDate = this.focusStart
          .clone()
          .startOf('day')
          .format('YYYY-MM-DDTHH:mm:ssZ')
        const endDate = this.focusStart
          .clone()
          .add(this.daysInWindow - 1, 'days')
          .endOf('day')
          .format('YYYY-MM-DDTHH:mm:ssZ')

        this.fetchCalendarAppointments({
          startDate,
          endDate,
          hostIds: this.hostIds,
          realtyIds: this.realtyIds,
        })
      },

      prev () {
        if (this.isAtMinimum) return
        const candidate = this.focusStart.clone().subtract(this.daysInWindow, 'days')
        const today = moment().startOf('day')
        // não deixa passar do dia atual
        this.focusStart = candidate.isBefore(today) ? today : candidate
      },

      next () {
        this.focusStart = this.focusStart.clone().add(this.daysInWindow, 'days')
      },

      getEventColor (event) {
        return event.color
      },

      statusChipClass (status) {
        return handleColors(status)
      },
    },
  }
</script>

<style scoped>
.appointment__calendar {
  position: fixed;
  top: 70px;
  right: 0;
  z-index: 2;
  width: 680px;
  max-height: calc(100vh - 100px);
  min-height: calc(100% - 70px);
  overflow-y: auto;
  padding: 15px;
  border-radius: 8px 0 0 0;
  background-color: #f0f3f4;
}

.appointment__calendar-event {
  height: 100%;
  overflow: hidden;
  font-size: 10px;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.87);
}

.appointment__calendar-event--assumed {
  border: 1px dashed rgba(0, 0, 0, 0.45);
  border-radius: 4px;
}

.appointment__calendar-event-neighborhood {
  font-size: 11px;
  font-weight: 700;
}

.appointment__calendar-event-line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.appointment__calendar-event-address {
  /* endereço completo: pode quebrar em várias linhas */
  overflow-wrap: anywhere;
}

.appointment__calendar-tooltip {
  position: relative;
  max-width: 240px;
  font-size: 12px;
  text-align: left;
}

/* seta apontando para o card (balão de fala) — fica no lado direito
   do tooltip (que abre à esquerda do card), apontando para o card.
   Deslocada além do padding do .v-tooltip__content (16px) + 5px. */
.appointment__calendar-tooltip-arrow {
  position: absolute;
  top: 50%;
  right: -21px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border: 5px solid transparent;
  border-left-color: #424242;
}

.appointment__calendar-price {
  min-width: 150px;
}

.appointment__calendar-toggle >>> .v-btn {
  height: auto !important;
  min-width: 0 !important;
  padding: 8px 12px !important;
  font-size: 6px !important;
  letter-spacing: 0;
}

.pulse-effect {
  animation: pulse 1.5s ease-out infinite;
  border-radius: 50% !important;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0px rgb(255, 0, 0, 1);
  }
  100% {
    box-shadow: 0 0 0 3px rgba(255, 0, 0, 0);
  }
}

/* esconde o painel onde o summary vira gaveta */
@media screen and (max-width: 1219px) {
  .appointment__calendar {
    display: none;
  }
}
</style>
